import { Tooltip } from '@mui/material';
import React, { useContext, useMemo } from 'react';
import { H } from '../../common/Layout';
import { UserContext } from '../../common/auth/UserContext';
import CustomAvatar from '../../common/components/CustomAvatar';
import { SIZES } from '../../common/constants/constants';
import { useI18n } from '../../common/i18n/I18n';
import { Icons } from '../../common/icons/Icons';
import {
    LIGHTING_PROFESSIONAL,
    MARKETPLACE,
    PLANNING,
    PRODUCTION_CONNECT,
    PRODUCTION_PROFESSIONAL,
    PROFILES,
    RESERVATION_PROFESSIONAL,
    SUPPLIER
} from '../../common/nav/apps';
import { toLocaleDateString } from '../../common/utils/dateTime';
import useHistory from '../../common/utils/useHistory';

export default (isProductionMode: boolean, hiddenColumns: string[] = []) => {
    const { application } = useContext(UserContext);
    const { changeRoute } = useHistory();
    const t = useI18n();
    const columnsAll = useMemo(() => {
        const headerNames = {
            title: t('grid.header.title'),
            aka: t('grid.header.AKA'),
            type: t('grid.header.type'),
            status: t('grid.header.status'),
            productionStatus: t('grid.header.production.status'),
            projectStatus: t('grid.header.project.status'),
            associatedTitle: t('grid.header.associated.title'),
            releaseDate: t('grid.header.release.date'),
            releaseYear: t('grid.header.release.year'),
            productionYears: t('grid.header.production.years'),
            season: t('grid.header.season'),
            createdBy: t('grid.header.createdBy'),
            createdOn: t('grid.header.createdOn'),
            updatedOn: t('grid.header.updatedOn')
        };

        return {
            managed: {
                field: 'managed',
                headerName: '',
                minWidth: 50,
                width: 50,
                resizable: false,
                disableReorder: true,
                hide: !isProductionMode,
                renderCell: (params) => {
                    return (
                        params.row?.managedProduction && (
                            <H sx={{ gap: 0.5, justifyContent: 'center' }}>
                                <Icons.Workspaces color={'success'} fontSize={'small'} />
                            </H>
                        )
                    );
                }
            },
            createdFrom: {
                field: 'createdFrom',
                headerName: '',
                minWidth: 50,
                headerClassName: 'hideRightSeparator',
                width: 50,
                resizable: false,
                disableReorder: true,
                sortable: false,
                disableColumnMenu: true,
                renderCell: (params) => {
                    const submissionCreatedFrom = params.row?.createdFrom;
                    if (Boolean(submissionCreatedFrom?.id && !submissionCreatedFrom?.deleted)) {
                        return (
                            <H sx={{ gap: 0.5, justifyContent: 'center', height: '100%' }}>
                                <Tooltip title={t('pipeline.view.submission')}>
                                    <Icons.SubmissionsPipeline
                                        color="primary"
                                        fontSize={SIZES.small}
                                        onClick={() => {
                                            changeRoute(`${application.home}/pipeline`, {
                                                submissionId: submissionCreatedFrom.id
                                            });
                                        }}
                                        sx={{ cursor: 'pointer' }}
                                    />
                                </Tooltip>
                            </H>
                        );
                    }
                    return null;
                }
            },
            avatar: {
                field: 'avatar',
                headerName: '',
                minWidth: 50,
                width: 50,
                resizable: false,
                disableReorder: true,
                renderCell: (params) => {
                    return (
                        params.row?.name && (
                            <H sx={{ gap: 0.5, justifyContent: 'center' }}>
                                <CustomAvatar label={params.row?.name} />
                            </H>
                        )
                    );
                }
            },
            name: {
                field: 'publictitle',
                headerName: headerNames.title,
                flex: 2,
                minWidth: 100,
                hide: hiddenColumns.includes(headerNames.title)
            },
            aka: {
                field: 'otherTitles',
                headerName: headerNames.aka,
                flex: 1,
                minWidth: 100,
                hide: hiddenColumns.includes(headerNames.aka)
            },
            type: {
                field: 'productiontype',
                headerName: headerNames.type,
                flex: 1,
                minWidth: 100,
                hide: hiddenColumns.includes(headerNames.type)
            },
            productionStatus: {
                field: 'productionStatus',
                headerName: headerNames.productionStatus,
                flex: 1,
                minWidth: 100,
                hide: hiddenColumns.includes(headerNames.productionStatus)
            },
            projectStatus: {
                field: 'projectStatus',
                headerName: headerNames.projectStatus,
                hide: isProductionMode || hiddenColumns.includes(headerNames.projectStatus),
                flex: 1,
                minWidth: 100
            },
            productionGroup: {
                field: 'productionGroup',
                headerName: headerNames.associatedTitle,
                flex: 1,
                hide: true,
                minWidth: 100
            },
            releaseDate: {
                field: 'releaseDate',
                headerName: headerNames.releaseDate,
                flex: 1,
                hide: true,
                minWidth: 100,
                type: 'date',
                valueFormatter: (value) => value && toLocaleDateString(value)
            },
            releaseYear: {
                field: 'releaseYear',
                headerName: headerNames.releaseYear,
                flex: 1,
                hide: true,
                minWidth: 100
            },
            productionYears: {
                field: 'productionYears',
                headerName: headerNames.productionYears,
                flex: 1,
                minWidth: 100,
                hide: hiddenColumns.includes(headerNames.productionYears)
            },
            season: {
                field: 'seasonNumber',
                headerName: headerNames.season,

                flex: 1,
                minWidth: 100,
                hide: hiddenColumns.includes(headerNames.season)
            },
            createdOn: {
                field: 'createdOn',
                headerName: headerNames.createdOn,
                flex: 1,
                type: 'date',
                minWidth: 100,
                valueFormatter: (value) => value && toLocaleDateString(value),
                hide: hiddenColumns.includes(headerNames.createdOn)
            },
            updatedOn: {
                field: 'updatedOn',
                headerName: headerNames.updatedOn,
                type: 'date',
                flex: 1,
                minWidth: 100,
                valueFormatter: (value) => value && toLocaleDateString(value),
                hide: hiddenColumns.includes(headerNames.updatedOn)
            },
            createdByName: {
                field: 'createdByName',
                headerName: headerNames.createdBy,
                flex: 1,
                minWidth: 100,
                hide: hiddenColumns.includes(headerNames.createdBy)
            }
        };
    }, [application?.home, changeRoute, hiddenColumns, isProductionMode, t]);

    const columnsByApp = useMemo(() => {
        return {
            [MARKETPLACE.id]: [
                columnsAll.managed,
                columnsAll.avatar,
                columnsAll.name,
                columnsAll.aka,
                columnsAll.type,
                columnsAll.productionStatus,
                columnsAll.productionGroup,
                columnsAll.releaseDate,
                columnsAll.releaseYear,
                columnsAll.productionYears,
                columnsAll.season,
                columnsAll.createdOn,
                columnsAll.updatedOn,
                columnsAll.createdByName
            ],
            [SUPPLIER.id]: [
                columnsAll.managed,
                columnsAll.avatar,
                columnsAll.name,
                columnsAll.aka,
                columnsAll.type,
                columnsAll.productionStatus,
                columnsAll.productionGroup,
                columnsAll.releaseDate,
                columnsAll.releaseYear,
                columnsAll.productionYears,
                columnsAll.season,
                columnsAll.createdOn,
                columnsAll.updatedOn,
                columnsAll.createdByName
            ],
            [PLANNING.id]: [
                columnsAll.managed,
                columnsAll.createdFrom,
                columnsAll.avatar,
                columnsAll.name,
                columnsAll.aka,
                columnsAll.type,
                columnsAll.productionStatus,
                columnsAll.projectStatus,
                columnsAll.productionGroup,
                columnsAll.releaseDate,
                columnsAll.releaseYear,
                columnsAll.productionYears,
                columnsAll.season,
                columnsAll.createdOn,
                columnsAll.updatedOn,
                columnsAll.createdByName
            ],
            [PROFILES.id]: [
                columnsAll.managed,
                columnsAll.avatar,
                columnsAll.name,
                columnsAll.aka,
                columnsAll.type,
                columnsAll.productionStatus,
                columnsAll.productionGroup,
                columnsAll.releaseDate,
                columnsAll.releaseYear,
                columnsAll.productionYears,
                columnsAll.season,
                columnsAll.createdOn,
                columnsAll.updatedOn,
                columnsAll.createdByName
            ],
            [PRODUCTION_PROFESSIONAL.id]: [
                columnsAll.managed,
                columnsAll.avatar,
                columnsAll.name,
                columnsAll.aka,
                columnsAll.type,
                columnsAll.productionStatus,
                columnsAll.productionGroup,
                columnsAll.releaseDate,
                columnsAll.releaseYear,
                columnsAll.productionYears,
                columnsAll.season,
                columnsAll.createdOn,
                columnsAll.updatedOn,
                columnsAll.createdByName
            ],
            [LIGHTING_PROFESSIONAL.id]: [
                columnsAll.managed,
                columnsAll.avatar,
                columnsAll.name,
                columnsAll.aka,
                columnsAll.type,
                columnsAll.productionStatus,
                columnsAll.productionGroup,
                columnsAll.releaseDate,
                columnsAll.releaseYear,
                columnsAll.productionYears,
                columnsAll.season,
                columnsAll.createdOn,
                columnsAll.updatedOn,
                columnsAll.createdByName
            ],
            [PRODUCTION_CONNECT.id]: [
                columnsAll.managed,
                columnsAll.avatar,
                columnsAll.name,
                columnsAll.aka,
                columnsAll.type,
                columnsAll.productionStatus,
                columnsAll.productionGroup,
                columnsAll.releaseDate,
                columnsAll.releaseYear,
                columnsAll.productionYears,
                columnsAll.season,
                columnsAll.createdOn,
                columnsAll.updatedOn,
                columnsAll.createdByName
            ],
            [RESERVATION_PROFESSIONAL.id]: [
                columnsAll.managed,
                columnsAll.avatar,
                columnsAll.name,
                columnsAll.aka,
                columnsAll.type,
                columnsAll.productionStatus,
                columnsAll.productionGroup,
                columnsAll.releaseDate,
                columnsAll.releaseYear,
                columnsAll.productionYears,
                columnsAll.season,
                columnsAll.createdOn,
                columnsAll.updatedOn,
                columnsAll.createdByName
            ]
        };
    }, [columnsAll]);

    return columnsByApp[application.id] ?? [];
};
